<template>
	<form @submit.prevent="submit" @keydown="form.errors.clear($event.target.id)">
		<slot
            :active-locale="activeLocale"
            :change-locale="setLocale"
            :available-locales="availableLocales"
        ></slot>

		<div v-if="standardButton" class="field">
			<g-button :loading="isLoading">
				<slot name="submit"></slot>
			</g-button>
		</div>
	</form>
</template>

<script>
import clone from 'lodash/clone'
import isEmpty from 'lodash/isEmpty'
import debounce from 'lodash/debounce'

export default {
        props:    {
            action:      '',
            method:      {default: 'post'},
            prepopulate: {default: false},
            prepopulateWithPostResponse: {default: false},
            before:      {default: false},
            afterError:  {default: false},
            form:        {default: false},
            message:     {default: false},

            idempotency: { type: Boolean, default: false },
            idempotencyKey: { type: String },

            initialLocale: {
                type: String,
                default: null
            }
        },
        data () {
            return {
                isLoading:   false,
                isValidating: false,
                formWatcher: null,
                activeLocale: this.initialLocale || this.$i18n.locale
            }
        },
        computed: {
            availableLocales() {
                return this.$store.getters['application/availableLocalesTranslated']
            },
            standardButton () {
                return !!this.$slots['submit']
            },
        },

        methods: {
            getIdempotencyKey() {
                /**
                 * When an idempotency key is provided, we want to make sure to use that. This, for example, can be
                 * used to have persistent idempotency keys across multiple form submissions or requests.
                 */
                if (this.idempotencyKey) {
                    return this.idempotencyKey;
                }

                return 'xxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
                    const r = Math.random() * 16 | 0;
                    const v = c === 'x' ? r : (r & 0x3 | 0x8);
                    return v.toString(16);
                });
            },
            setLoadingState (state) {
                this.$emit('loading-state', state)
                this.isLoading = state
            },
            validate: debounce(function () {
                if (this.isValidating) return;

                this.isValidating = true;
                const html5Errors = {};

                try {
                    if (this.form.errors.has('_fromServer') && !this.form.errors.get('_fromServer')) {
                        this.form.errors.clear();
                    }

                    this.$children.forEach(child => {
                        const element = child.$el.querySelector('input');
                        if (element && !element.name && !element.checkValidity()) {
                            html5Errors[child.name] = [element.validationMessage];
                        }
                    });

                    if (!isEmpty(html5Errors)) {
                        html5Errors._fromServer = [false];
                        this.form.errors.record(html5Errors);
                    }

                    this.$emit('changed', this.form);
                } finally {
                    this.isValidating = false;
                }
            }, 1000),

            setLocale(locale) {
                this.activeLocale = locale
                this.form.setLocale(locale)
                this.$emit('locale-changed', locale)
            },

            submit (event) {
                return new Promise(async (resolve) => {
                    this.setLoadingState(true);

                    let sendData = await this.getFormData()
                        .catch(() => {
                            this.$emit('loading-state', false)

                            this.setLoadingState(false)
                        })

                    if (typeof this.$attrs.files !== 'undefined') {
                        sendData.config = {
                            headers: {'content-type': 'multipart/form-data'}
                        }
                    }

                    // When idempotency is enabled, we append a special header to the request which tells our API
                    // to treat this request as idempotent
                    if (sendData !== false && this.idempotency === true) {
                        sendData.config = sendData.config || {};
                        sendData.config.headers = {
                            ...sendData.config.headers,
                            'X-Gifty-Idempotency-Key': this.getIdempotencyKey()
                        };
                    }

                    if (sendData !== false) {
                        sendData.submit(this.method, this.action)
                           .then(data => {
                               this.form.errors.clear()
                               this.$emit('success', data)

                               if (!!this.message) {
                                   this.$buefy.toast.open({
                                       message: this.message,
                                       type:    'is-success'
                                   })
                               }

                               if (!!this.prepopulate && !!this.prepopulateWithPostResponse) {
                                   this.form.setFormData(data).then(event => {
                                       this.$emit('prepopulated', event)
                                   })
                               }

                               this.setLoadingState(false);

							   resolve()
                           })
                           .catch(async error => {
                               let errorData = error.response.data

                               // Allow afterError callback to transform the errors before they are recorded
                               if (this.afterError && errorData.errors) {
                                   const transformedErrors = await this.afterError({...errorData.errors})
                                   if (transformedErrors) {
                                       errorData = {...errorData, errors: transformedErrors}
                                       // Re-record the transformed errors
                                       this.form.setErrors(transformedErrors)
                                   }
                               }

                               this.$emit('fail', errorData)

                               if (error.response.status === 422 && errorData.message !== undefined) {
                                   this.$buefy.toast.open({
                                       message: this.$t('notice.invalid') + ': ' + errorData.message,
                                       type:    'is-danger',
                                        duration: 1000 * 3,
                                   })
                               } else {
                                   this.$buefy.toast.open({
                                       message: this.$t('notice.error'),
                                       type:    'is-danger'
                                   })
                               }

                               this.setLoadingState(false)

							   resolve()
                           })
                    }
                })
            },
            getFormData() {
                return new Promise(async (resolve, reject) => {
                    let sendData = clone(this.form)

                    if (!!this.before) {
                        sendData = await this.before(sendData, event)

                        if (sendData === false) {
                            reject()
                        } else if (!(sendData instanceof Form)) {
                            sendData = this.form
                        }
                    }

                    resolve(sendData)
                })
            },
            getParsedFormData: async function () {
                let form = await this.getFormData()

                return form.data('post')
            },
        },
        created() {
            // Initialize form with current locale
            this.form.setLocale(this.activeLocale)

            // Watcher that handles all form changes
            this.$watch(
                'form',
                () => {
                    if (!this.isValidating) {
                        this.validate();
                    }
                },
                { deep: true }
            );
        },
        provide() {
            return {
                formLocale: () => this.activeLocale,
                isTranslatable: (fieldName) => {
                    return this.form.isFieldTranslatable(fieldName)
                }
            }
        },
        mounted () {
            if (!!this.prepopulate) {
                this.form.prepopulate(this.action).then(event => {
                    this.$emit('prepopulated', event)
                })
            }
        }
    }
</script>
